import { Download, Settings2 } from 'lucide-react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { useTranslation, Trans } from 'react-i18next';
import { ParameterItemForm } from '@/Domain/components/Action/CampaignAction/Parameter/ParameterItemForm';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { FormDescription, FormLabel } from '@/Domain/components/Shared/Form/Form';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import AddIcon from '@/Domain/components/icons/AddIcon';
import { CampaignForm } from '@/Domain/hooks/Action/CampaignAction/useCampaignActionForm';

type Props = {
  form: UseFormReturn<CampaignForm>;
};

const MAX_PARAMETERS = 10;
export const ParameterList = ({ form }: Props) => {
  const { t } = useTranslation('campaigns');
  const { remove, append, fields } = useFieldArray({
    control: form.control,
    name: 'parameters',
    keyName: 'field_id',
  });

  const handleCreate = () => {
    append({ name: '', description: '', data_format: '', is_enabled: false });
  };

  const handelDelete = (index: number) => remove(index);
  const maxParametersReached = fields.length >= MAX_PARAMETERS;

  return (
    <div id="data-to-collect">
      <FormLabel>{t('form.data_to_collect_label')}</FormLabel>
      <FormDescription className="mb-5">{t('form.date_to_collect_description')}</FormDescription>
      <Alert variant="info" className="my-5">
        <AlertTitle>
          <Download className="size-4" />
          {t('form.export_alert.title')}
        </AlertTitle>
        <AlertDescription>
          <Trans
            i18nKey="campaigns:form.export_alert.description"
            components={{ icon: <Settings2 className="inline size-4" /> }}
          />
        </AlertDescription>
      </Alert>

      {/* Mobile: this 6-column grid is wider than any phone viewport, so we
          wrap it in a horizontal scroll. The min-w-[600px] guarantees the grid
          renders at its desktop width even on a 375px screen. */}
      <div className="-mx-1 overflow-x-auto">
        <div className="min-w-[600px]">
          <section className="grid grid-cols-[minmax(100px,0.75fr)_minmax(150px,1fr)_minmax(100px,0.75fr)_65px_5px_34px] items-center gap-4 border-b border-border bg-accent px-4 py-3 text-sm font-medium text-muted-foreground">
            <span>{t('form.parameter_table.header.name')}</span>
            <span>{t('form.parameter_table.header.description')}</span>
            <span>{t('form.parameter_table.header.format')}</span>
            <span>{t('form.parameter_table.header.state')}</span>
            <span />
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button
                    size="icon"
                    onClick={handleCreate}
                    className="justify-self-end"
                    disabled={maxParametersReached}
                  >
                    <AddIcon />
                  </Button>
                </div>
              </TooltipTrigger>
              <TooltipContent align="end">
                {maxParametersReached
                  ? t('tooltip.max_parameters_reached', { max: MAX_PARAMETERS })
                  : t('tooltip.add')}
              </TooltipContent>
            </Tooltip>
          </section>

          {fields.map((field, index) => (
            <ParameterItemForm key={field.id} index={index} onRemove={handelDelete} />
          ))}
        </div>
      </div>
    </div>
  );
};
